// Status codes that originate in our pooled-fit wrapper, not the analyzer
// library. These take precedence.
const POOLED_WRAPPER_MESSAGES = {
    "-2": "Invalid input. Concentrations or signal arrays are empty, lengths don't match, or contain non-numeric values.",
    "-3": "Too few standards. At least 3 levels are required for a fit (5 levels recommended).",
    "-4": "Pooling failed. One or more RCC columns have signals that can't be pooled - check for zero, negative, or wildly inconsistent values."
};

// Analyzer-library M_RTN_CODE values. Descriptions paraphrased from the
// G06 Error Messages.docx so they're actionable for the QAT user rather
// than spec-language.
const RTN_CODE_MESSAGES = {
    "0":  "Function completed successfully.",
    "2":  "Check result. One or more replicate signals were below the lower limit (1.00E-09 ALU, or 1.00E-06 ALU for semi-quantitative).",
    "3":  "Signal above curve. The signal to be predicted is above the upper asymptote - parameter B/C ratio or signal > 1.00E+31.",
    "4":  "Signal below curve. The normalised signal is at or below zero, so it falls below the curve's lower asymptote.",
    "5":  "Fit completed with status flags set. One or more StatusReport bits were raised during the fit - see the backend log for ESTIM_*/LOGISTIC_*/TABULATE_FAIL details.",
    "8":  "Bad low signal. One or more input signals are below 1.00E-09 ALU.",
    "9":  "Bad high signal. One or more input signals are above 1.00E+11 ALU.",
    "10": "Predicted concentration was below 1.00E-09 - back-prediction out of range.",
    "11": "Predicted concentration was above 1.00E+11 - back-prediction out of range.",
    "16": "Trend mismatch. The protocol trend (ascending / descending) doesn't match the fitted curve's trend.",
    "17": "Missing standard. A calibrator has zero or a negative number of replicates.",
    "18": "Too few standards. The number of master calibrators is less than the 5 parameters in the protein-5 fit.",
    "19": "Logistic fit failed. The 5-parameter fit could not converge. Common causes: parameter N exceeded 5, solution-matrix diagonal element fell below 1.00E-29, more than 50 total iterations across all attempted fits, B/C ratio outside (0,1) for an immunometric assay, parameter A greater than Y0, or tabulated curve values not monotonic.",
    "20": "Could not fit IGCC pseudocalibrators. The IGCC re-calibration step (derive_cal_ref) failed - solution-matrix diagonal fell below 1.00E-29, or parameter N exceeded 5 while searching for a fit to the calibrator signals + MGCC concentrations.",
    "22": "Bad prediction. None of the signal replicates given for prediction were in the valid range (1.00E-09 to 1.00E+11), or the replicate count was zero.",
    "25": "Array limit exceeded. Internal limit of 20 calibrators, 30 pooled curves, or 32 replicates was exceeded.",
    "26": "Math error. A divide-by-zero or floating-point overflow was detected during the fit.",
    "27": "Invalid argument. A mathematically impossible operation (e.g. sqrt of a negative) or an invalid input was passed to the library.",
    "28": "Concentrations not in increasing order. The first calibrator must be zero and subsequent levels must strictly increase; no two adjacent levels may share a value.",
    "29": "Signals not monotonic. Calibrator signals must run ascending (immunometric) or descending (competitive); no two adjacent levels may share a value.",
    "30": "Signal at or below zero. One or more calibrator mean signals are zero or negative.",
    "34": "Bad calibration. Replicate spread exceeded the configured limit for one or more calibrators, or a replicate signal fell below 1.00E-09 (1.00E-05 for semi-quantitative)."
};

/**
 * Map a backend status code (from /api/v1/rawcurve/fitpooled response) to
 * a user-facing message. Tries the pooled-wrapper map first, then the
 * analyzer M_RTN_CODE map, then falls back to a generic placeholder so
 * the dialog never renders a bare number.
 *
 * @param {number|string} status   the `status` field from the JSON response
 * @param {string} [serverMessage] the response's `message` field, if any
 * @returns {string}
 */
export const describeG06Status = (status, serverMessage) => {
    if (status === undefined || status === null) {
        return serverMessage || "Backend did not return a status code.";
    }
    const key = String(status);
    return POOLED_WRAPPER_MESSAGES[key]
        || RTN_CODE_MESSAGES[key]
        || serverMessage
        || `Fit failed with status ${key}. See backend log for details.`;
};

/**
 *
 * @param {number|string} status
 * @returns {string}
 */
export const titleForG06Status = (status) => {
    const key = String(status);
    if (POOLED_WRAPPER_MESSAGES[key]) return "RCC Data Rejected";
    switch (key) {
        case "16": return "Curve Trend Mismatch";
        case "17": case "18": return "Insufficient Calibrators";
        case "19": return "Curve Fit Failed";
        case "20": return "IGCC Fit Failed";
        case "22": case "10": case "11": return "Prediction Out of Range";
        case "25": return "Internal Array Limit Exceeded";
        case "26": case "27": return "Math Error";
        case "28": return "Concentration Order Invalid";
        case "29": return "Signal Order Invalid";
        case "30": return "Signal At or Below Zero";
        case "34": return "Bad Calibration";
        case "8":  case "9": return "Signal Out of Range";
        case "2":  case "3":  case "4": return "Curve Boundary Warning";
        default: return "Curve Fit Failed";
    }
};
